import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, Loader2, Waves, Eye, EyeOff, ChevronDown, ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import ImageDropUpload from '../../components/admin/ImageDropUpload';
import MultiImageDropUpload from '../../components/admin/MultiImageDropUpload';
import { invalidateSettingsCache } from '../../lib/settingsCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: 'north', label: 'North Shore' },
  { key: 'south', label: 'South Shore' },
];

const blankBeach = () => ({
  name: '', description: '', image_url: '', gallery: [], map_url: '',
  section: 'north', sort_order: 0, is_active: true,
});

const BeachEditor = ({ beach, onChange, onSave, onDelete, saving, isOpen, onToggle }) => {
  const id = beach.id || 'new';
  const gallery = Array.isArray(beach.gallery) ? beach.gallery : [];
  return (
    <Card data-testid={`beach-editor-${id}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button type="button" onClick={onToggle}
              className="p-1 rounded text-slate-500 hover:text-blue-700 hover:bg-slate-100 transition-colors"
              aria-expanded={isOpen} aria-label={isOpen ? 'Collapse beach' : 'Expand beach'}
              data-testid={`beach-accordion-${id}`}>
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <Waves className="w-5 h-5 text-blue-600 shrink-0" />
            <button type="button" onClick={onToggle} className="text-left min-w-0">
              <CardTitle className="text-base truncate">{beach.name || 'New Beach'}</CardTitle>
            </button>
            {beach.is_active ? (
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> Visible
              </span>
            ) : (
              <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <EyeOff className="w-3 h-3" /> Hidden
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onChange({ ...beach, is_active: !beach.is_active })}
              data-testid={`beach-toggle-active-${id}`}>
              {beach.is_active ? 'Hide' : 'Show'}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} data-testid={`beach-save-${id}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={onDelete}
              data-testid={`beach-delete-${id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          {SECTIONS.find((s) => s.key === beach.section)?.label} &middot; {gallery.length} gallery photo{gallery.length === 1 ? '' : 's'}
          &middot; order {beach.sort_order ?? 0}
          {!isOpen && ' \u2014 click the chevron to edit'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4" hidden={!isOpen}>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Shore Section</Label>
            <select value={beach.section} onChange={(e) => onChange({ ...beach, section: e.target.value })}
              className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              data-testid={`beach-section-${id}`}>
              {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Beach Name</Label>
            <Input value={beach.name} onChange={(e) => onChange({ ...beach, name: e.target.value })}
              placeholder="e.g. Trunk Bay" className="mt-1" data-testid={`beach-name-${id}`} />
          </div>
          <div>
            <Label>Order Number</Label>
            <Input type="number" value={beach.sort_order}
              onChange={(e) => onChange({ ...beach, sort_order: Number(e.target.value) || 0 })}
              className="mt-1" data-testid={`beach-order-${id}`} />
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <Textarea value={beach.description} onChange={(e) => onChange({ ...beach, description: e.target.value })}
            rows={5} className="mt-1" data-testid={`beach-description-${id}`} />
          <p className="text-xs text-gray-500 mt-1">Blank lines start a new paragraph.</p>
        </div>

        <div>
          <Label className="flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Map Link (optional)</Label>
          <Input value={beach.map_url} onChange={(e) => onChange({ ...beach, map_url: e.target.value })}
            placeholder="https://maps.google.com/..." className="mt-1" data-testid={`beach-map-${id}`} />
        </div>

        <div className="max-w-md">
          <ImageDropUpload
            label="Main Photo"
            hint="shown in the beach row"
            value={beach.image_url}
            folder="beaches/main"
            aspect="aspect-video"
            objectFit="object-cover"
            testId={`beach-photo-drop-${id}`}
            onUploaded={(url) => onChange({ ...beach, image_url: url })}
          />
        </div>

        <MultiImageDropUpload
          label="Photo Gallery"
          hint="powers the See Gallery button"
          value={gallery}
          folder="beaches/gallery"
          testId={`beach-gallery-${id}`}
          onChange={(next) => onChange({ ...beach, gallery: next })}
        />
      </CardContent>
    </Card>
  );
};

const AdminBeachInfo = () => {
  const [beaches, setBeaches] = useState([]);
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [savingPage, setSavingPage] = useState(false);
  const [activeTab, setActiveTab] = useState('north');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const bustPublicCache = () =>
    invalidateSettingsCache(`${process.env.REACT_APP_BACKEND_URL}/api/public/beaches`);

  const load = async () => {
    try {
      const [bRes, pRes] = await Promise.all([
        axios.get(`${API}/beaches/list`, { headers: authHeaders() }),
        axios.get(`${API}/beaches/page`, { headers: authHeaders() }),
      ]);
      setBeaches(Array.isArray(bRes.data) ? bRes.data : []);
      setPage(pRes.data || {});
    } catch (error) {
      toast({
        title: 'Load failed',
        description: error.response?.data?.detail || 'Could not load beaches.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveBeach = async (beach) => {
    if (!beach.name?.trim()) {
      toast({ title: 'Name required', description: 'Give the beach a name first.', variant: 'destructive' });
      return;
    }
    setSavingId(beach.id || 'new');
    try {
      const payload = {
        name: beach.name,
        description: beach.description || '',
        image_url: beach.image_url || '',
        gallery: Array.isArray(beach.gallery) ? beach.gallery : [],
        map_url: beach.map_url || '',
        section: beach.section,
        sort_order: Number(beach.sort_order) || 0,
        is_active: Boolean(beach.is_active),
      };
      if (beach.id) {
        await axios.put(`${API}/beaches/list/${beach.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/beaches/list`, payload, { headers: authHeaders() });
        setDraft(null);
      }
      bustPublicCache();
      toast({ title: 'Saved', description: `${payload.name} updated.` });
      await load();
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save the beach.',
        variant: 'destructive',
      });
    } finally {
      setSavingId(null);
    }
  };

  const deleteBeach = async (beach) => {
    if (!beach.id) { setDraft(null); return; }
    if (!window.confirm(`Delete "${beach.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/beaches/list/${beach.id}`, { headers: authHeaders() });
      bustPublicCache();
      toast({ title: 'Deleted', description: `${beach.name} removed.` });
      await load();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Could not delete the beach.',
        variant: 'destructive',
      });
    }
  };

  const savePage = async () => {
    setSavingPage(true);
    try {
      const { data } = await axios.put(`${API}/beaches/page`, page, { headers: authHeaders() });
      setPage(data || {});
      bustPublicCache();
      toast({ title: 'Saved', description: 'Page content updated.' });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save page content.',
        variant: 'destructive',
      });
    } finally {
      setSavingPage(false);
    }
  };

  const visible = useMemo(() => beaches.filter((b) => (b.section || 'north') === activeTab), [beaches, activeTab]);
  const counts = useMemo(() => {
    const map = {};
    SECTIONS.forEach((s) => { map[s.key] = beaches.filter((b) => (b.section || 'north') === s.key).length; });
    return map;
  }, [beaches]);

  if (loading || !page) {
    return (
      <div className="flex justify-center py-24" data-testid="beach-admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="admin-beach-info">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Waves className="w-7 h-7 text-blue-600" />
            Island Beach Info
          </h2>
          <p className="text-gray-500 mt-1">
            {beaches.length} beach{beaches.length === 1 ? '' : 'es'}. Turn the component on or off with
            the <strong>beaches_enabled</strong> feature flag.
          </p>
        </div>
        {!draft && (
          <Button onClick={() => { setDraft({ ...blankBeach(), section: activeTab }); setOpenId('new'); }}
            data-testid="beach-add">
            <Plus className="w-4 h-4 mr-2" /> Add Beach
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3">
        {SECTIONS.map((s) => (
          <button key={s.key} onClick={() => setActiveTab(s.key)}
            className={`px-3 py-2 text-sm font-bold rounded-t transition-colors ${
              activeTab === s.key ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                                 : 'text-gray-600 hover:text-blue-700 hover:bg-gray-50'}`}
            data-testid={`beach-tab-${s.key}`}>
            {s.label} <span className="text-xs font-normal">({counts[s.key] || 0})</span>
          </button>
        ))}
      </div>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Page Content</CardTitle>
              <CardDescription>Headings and intro copy for the public page.</CardDescription>
            </div>
            <Button size="sm" onClick={savePage} disabled={savingPage} data-testid="beach-save-page">
              {savingPage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Content
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Intro Title</Label>
              <Input value={page.intro_title || ''} onChange={(e) => setPage({ ...page, intro_title: e.target.value })}
                className="mt-1" data-testid="beach-intro-title" />
            </div>
            <div>
              <Label>North Shore Heading</Label>
              <Input value={page.north_title || ''} onChange={(e) => setPage({ ...page, north_title: e.target.value })}
                className="mt-1" data-testid="beach-north-title" />
            </div>
            <div>
              <Label>South Shore Heading</Label>
              <Input value={page.south_title || ''} onChange={(e) => setPage({ ...page, south_title: e.target.value })}
                className="mt-1" data-testid="beach-south-title" />
            </div>
          </div>
          <div>
            <Label>Intro Body (optional)</Label>
            <Textarea value={page.intro_body || ''} onChange={(e) => setPage({ ...page, intro_body: e.target.value })}
              rows={3} className="mt-1" data-testid="beach-intro-body" />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {draft && (
          <BeachEditor beach={draft} onChange={setDraft} onSave={() => saveBeach(draft)}
            onDelete={() => setDraft(null)} saving={savingId === 'new'}
            isOpen={openId === 'new'} onToggle={() => setOpenId(openId === 'new' ? null : 'new')} />
        )}

        {visible.map((beach) => (
          <BeachEditor key={beach.id} beach={beach}
            onChange={(next) => setBeaches((prev) => prev.map((b) => (b.id === next.id ? next : b)))}
            onSave={() => saveBeach(beach)} onDelete={() => deleteBeach(beach)}
            saving={savingId === beach.id} isOpen={openId === beach.id}
            onToggle={() => setOpenId(openId === beach.id ? null : beach.id)} />
        ))}

        {!draft && visible.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center text-gray-500">
              No beaches in this section yet. Click <strong>Add Beach</strong> to create one.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminBeachInfo;
